/**
 * Service for managing data files.
 */
import supabase from './supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

export interface DataFile {
  id: string;
  user_id: string;
  name: string;
  data_type: string;
  file_category?: string;
  s3_path: string;
  sample_count?: number;
  project_id?: string | null;
  metadata?: any;
  created_at?: string;
  updated_at?: string;
}

class DataService {
  /**
   * Get authentication headers.
   */
  private async getAuthHeaders(): Promise<HeadersInit> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Not authenticated');
    }

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    };
  }

  /**
   * List all data files for the current user.
   */
  async listData(projectId?: string): Promise<DataFile[]> {
    const headers = await this.getAuthHeaders();
    const url = projectId 
      ? `${BACKEND_URL}/api/storage/data?project_id=${projectId}`
      : `${BACKEND_URL}/api/storage/data`;

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to list data files');
    }

    const data = await response.json();
    console.log('[DataService] Response:', data);
    return data.data_files || [];
  }

  /**
   * Get a specific data file by ID.
   */
  async getData(dataId: string): Promise<DataFile> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${BACKEND_URL}/api/storage/data/${dataId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get data file');
    }

    return response.json();
  }

  /**
   * Delete a data file.
   */
  async deleteData(dataId: string): Promise<void> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${BACKEND_URL}/api/storage/data/${dataId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete data file');
    }
  }

  /**
   * List files within a data group folder.
   */
  async listDataFiles(dataId: string): Promise<Array<{ name: string; path: string; signed_url: string }>> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${BACKEND_URL}/api/storage/data/${dataId}/files`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to list data files');
    }

    const data = await response.json();
    return data.files || [];
  }
}

export const dataService = new DataService();
export default dataService;

